import type { ReactNode } from 'react';
import { useApi, useRead } from '../context';
import { Failure, Loading } from '../bits';
import { useT } from '../../messages';
import type { BusinessTab } from '../router';
import { buildChecklist, nextStep } from './overview/steps';
import { ChecklistPanel } from './overview/Checklist';
import { NextStep } from './overview/NextStep';
import { RightNow } from './overview/RightNow';
import { LATELY_LINES, Lately } from './overview/Lately';
import type { Moves } from './overview/go';
import './overview/overview.css';

/**
 * **Overview** — where they are in getting live, what is next, and whether
 * anything is wrong (console redesign B1; the mockup's Overview panel).
 *
 * In the order an operator asks: **the next step**, first and alone; the
 * **"Getting live" checklist** it was taken from, each step computed from real
 * state with who it waits on and the way to where it gets done; **"Right
 * now"**, the health verdict with its reasons as sentences and their fixes;
 * and **"Lately"**, the last few changes in words.
 *
 * ## Three reads, and never more
 *
 * Opening a business costs the page's tenant read plus these three —
 * readiness (the checklist's facts, one statement), health (the verdict, nine
 * bounded statements) and one page of the activity log — four in all, pinned
 * by `overview.test.tsx`. Each is its own read so one that fails leaves the
 * others on screen, which is the reason the Health card had its own. Re-read
 * refreshes readiness and health together; the log is left alone.
 *
 * The moves (`onTab`, `onChecks`) are the page's: an action here changes the
 * address exactly as the tab bar does, so Back returns to the Overview.
 */
export function OverviewTab({
  tenantId,
  onTab,
  onChecks,
}: {
  tenantId: string;
  onTab?: ((tab: BusinessTab) => void) | undefined;
  onChecks?: (() => void) | undefined;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const readiness = useRead(() => api.tenantReadiness(tenantId), [tenantId]);
  const health = useRead(() => api.tenantHealth(tenantId), [tenantId]);
  const activity = useRead(
    () => api.activity(tenantId, { limit: LATELY_LINES, changesOnly: true }),
    [tenantId],
  );

  const moves: Moves = {
    onTab: (tab) => onTab?.(tab),
    onChecks: () => onChecks?.(),
  };

  const r = readiness.value;
  const list = r === null ? null : buildChecklist(t, r);
  const next = list === null || r === null ? null : nextStep(list, r.status);

  return (
    <div className="ov">
      {next === null ? null : <NextStep step={next} moves={moves} />}

      <div className="ov-grid">
        <div className="ov-col">
          {readiness.failure !== null ? <Failure failure={readiness.failure} /> : null}
          {list === null || r === null ? (
            readiness.loading ? (
              <Loading what={t('overview.loading.readiness')} />
            ) : null
          ) : (
            <ChecklistPanel list={list} status={r.status} moves={moves} />
          )}
        </div>
        <div className="ov-col">
          <RightNow
            health={health}
            readiness={r}
            onReread={() => {
              health.reload();
              readiness.reload();
            }}
            moves={moves}
          />
          <Lately activity={activity} onAll={() => moves.onTab('activity')} />
        </div>
      </div>
    </div>
  );
}
